/* =============================================================================
   ROO RIDERS â Global JavaScript
   Custom Shopify Theme Â· 2026
   ============================================================================= */

'use strict';

/* ---- 1. VEHICLE SELECTOR STATE ---- */
const RooVehicle = (() => {
  const STORAGE_KEY = 'rr_vehicle';

  // Vehicle â collection handle map
  const VEHICLES = {
    Toyota: {
      'Hiace 200 Series':  'toyota-hiace-200-300-series',
      'Hiace 300 Series':  'toyota-hiace-300-series-accessories',
      'Estima / Tarago':   'toyota-estima',
      'Alphard / Vellfire':'toyota-alphard',
    },
    Nissan: {
      'Elgrand E51': 'nissan-elgrand-e51-e52-series',
      'Elgrand E52': 'nissan-elgrand-e51-e52-series',
      'Serena':      'nissan-serena',
    },
    Mitsubishi: {
      'Delica D5': 'mitsubishi-delica-d5',
    },
    LDV: {
      'G10': 'ldv-g10',
    },
    Suzuki: {
      'K-Truck': 'suzuki-k-truck',
    },
    BYD: {
      'Shark 6': 'parts-accessories-for-byd-shark-6',
    },
  };

  function get() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch { return {}; }
  }

  function set(make, model) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ make, model }));
      document.dispatchEvent(new CustomEvent('rr:vehicle:changed', { detail: { make, model } }));
    } catch {}
  }

  function clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      document.dispatchEvent(new CustomEvent('rr:vehicle:changed', { detail: {} }));
    } catch {}
  }

  function getCollectionHandle(make, model) {
    return VEHICLES[make]?.[model] || null;
  }

  function getMakes() { return Object.keys(VEHICLES); }

  function getModels(make) { return make ? Object.keys(VEHICLES[make] || {}) : []; }

  return { get, set, clear, getCollectionHandle, getMakes, getModels, VEHICLES };
})();


/* ---- 2. VEHICLE FINDER WIDGET ---- */
const VehicleFinder = (() => {
  function init(containerEl) {
    if (!containerEl) return;

    const makeSelect  = containerEl.querySelector('[data-vf-make]');
    const modelSelect = containerEl.querySelector('[data-vf-model]');
    const yearSelect  = containerEl.querySelector('[data-vf-year]');
    const findBtn     = containerEl.querySelector('[data-vf-submit]');
    const chips       = containerEl.querySelectorAll('[data-vf-chip]');

    // Restore persisted vehicle
    const saved = RooVehicle.get();
    if (saved.make) {
      makeSelect.value = saved.make;
      _populateModels(makeSelect, modelSelect, saved.model);
    }

    // Make change â populate models
    makeSelect?.addEventListener('change', () => {
      _populateModels(makeSelect, modelSelect, null);
    });

    // Quick-pick chips
    chips.forEach(chip => {
      chip.addEventListener('click', () => {
        const make  = chip.dataset.vfMake;
        const model = chip.dataset.vfModel;
        if (makeSelect)  makeSelect.value = make;
        _populateModels(makeSelect, modelSelect, model);
      });
    });

    // Find button
    findBtn?.addEventListener('click', () => {
      const make  = makeSelect?.value;
      const model = modelSelect?.value;
      if (!make || !model) {
        _shake(findBtn);
        return;
      }
      RooVehicle.set(make, model);
      const handle = RooVehicle.getCollectionHandle(make, model);
      if (handle) {
        window.location.href = `/collections/${handle}`;
      }
    });
  }

  function _populateModels(makeSelect, modelSelect, preselect) {
    const make = makeSelect?.value;
    const models = RooVehicle.getModels(make);
    if (!modelSelect) return;

    modelSelect.innerHTML = '<option value="">Select Model</option>';
    models.forEach(m => {
      const opt = document.createElement('option');
      opt.value = m;
      opt.textContent = m;
      if (preselect && m === preselect) opt.selected = true;
      modelSelect.appendChild(opt);
    });
    modelSelect.disabled = models.length === 0;
  }

  function _shake(el) {
    el.classList.add('is-shaking');
    el.addEventListener('animationend', () => el.classList.remove('is-shaking'), { once: true });
  }

  return { init };
})();


/* ---- 3. MOBILE NAV ---- */
const MobileNav = (() => {
  function init() {
    const hamburger = document.querySelector('[data-hamburger]');
    const mobileNav = document.querySelector('[data-mobile-nav]');
    const overlay   = document.querySelector('[data-overlay]');

    if (!hamburger || !mobileNav) return;

    hamburger.addEventListener('click', () => toggle());

    overlay?.addEventListener('click', () => close());

    // Accordion sections within mobile nav
    const headings = mobileNav.querySelectorAll('[data-mobile-nav-heading]');
    headings.forEach(heading => {
      heading.addEventListener('click', () => {
        const sub = heading.nextElementSibling;
        const isOpen = heading.classList.contains('is-open');
        // Close all
        headings.forEach(h => {
          h.classList.remove('is-open');
          h.nextElementSibling?.classList.remove('is-open');
        });
        // Toggle clicked
        if (!isOpen) {
          heading.classList.add('is-open');
          sub?.classList.add('is-open');
        }
      });
    });

    function toggle() {
      const isOpen = mobileNav.classList.contains('is-open');
      isOpen ? close() : open();
    }
    function open() {
      mobileNav.classList.add('is-open');
      hamburger.classList.add('is-open');
      overlay?.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      hamburger.setAttribute('aria-expanded', 'true');
    }
    function close() {
      mobileNav.classList.remove('is-open');
      hamburger.classList.remove('is-open');
      overlay?.classList.remove('is-active');
      document.body.style.overflow = '';
      hamburger.setAttribute('aria-expanded', 'false');
    }
  }

  return { init };
})();


/* ---- 4. CART COUNT UPDATE ---- */
const CartBadge = (() => {
  function update(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'inline-flex' : 'none';
    });
  }

  async function refresh() {
    try {
      const res  = await fetch('/cart.js');
      const data = await res.json();
      update(data.item_count);
    } catch {}
  }

  return { update, refresh };
})();


/* ---- 5. WISHLIST (localStorage) ---- */
const Wishlist = (() => {
  const KEY = 'rr_wishlist';

  function get() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; }
    catch { return []; }
  }

  function toggle(productId) {
    const list = get();
    const idx  = list.indexOf(productId);
    if (idx === -1) { list.push(productId); }
    else { list.splice(idx, 1); }
    try { localStorage.setItem(KEY, JSON.stringify(list)); }
    catch {}
    return list.includes(productId);
  }

  function has(productId) { return get().includes(productId); }

  function initButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const id = parseInt(btn.dataset.wishlistBtn, 10);
      if (Wishlist.has(id)) btn.classList.add('is-active');

      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        const active = Wishlist.toggle(id);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      });
    });
  }

  return { get, toggle, has, initButtons };
})();


/* ---- 6. QUICK ADD TO CART ---- */
const QuickAdd = (() => {
  async function add(variantId, qty = 1) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await CartBadge.refresh();
      document.dispatchEvent(new CustomEvent('rr:cart:updated'));
      return await res.json();
    } catch (err) {
      console.warn('[RooRiders] Cart add error:', err);
    }
  }

  function initButtons() {
    document.querySelectorAll('[data-quick-add]').forEach(btn => {
      btn.addEventListener('click', async e => {
        e.preventDefault();
        const variantId = btn.dataset.quickAdd;
        if (!variantId) return;

        btn.disabled = true;
        btn.textContent = 'Addingâ¦';

        await add(parseInt(variantId, 10));

        btn.textContent = 'Added â';
        setTimeout(() => {
          btn.textContent = btn.dataset.originalText || 'Add to Cart';
          btn.disabled = false;
        }, 2000);
      });
    });
  }

  return { add, initButtons };
})();


/* ---- 7. SECTION-AWARE VEHICLE LABEL ---- */
function updateVehicleLabels() {
  const { make, model } = RooVehicle.get();
  document.querySelectorAll('[data-vehicle-label]').forEach(el => {
    if (model) {
      el.innerHTML = `Popular for <span>${model}</span>`;
    }
  });
}


/* ---- 8. INIT ---- */
document.addEventListener('DOMContentLoaded', () => {
  MobileNav.init();
  document.querySelectorAll('[data-vehicle-finder]').forEach(el => VehicleFinder.init(el));
  Wishlist.initButtons();
  QuickAdd.initButtons();
  CartBadge.refresh();
  updateVehicleLabels();
  document.addEventListener('rr:vehicle:changed', updateVehicleLabels);
});
